import React, { useCallback, useContext, useEffect, useState } from 'react';

import { ArrowRightAlt } from '@material-ui/icons';
import { Add, MeetingRoom, NoMeetingRoom, Person3 } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogProps,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { SubClassContainer } from './SubClassContainer';
import { SubClassesCreateDialog } from './SubClassesCreateDialog';
import { CLASS_DELETE, CREATE_LTI_CODE } from '../../config/apiPaths';
import { ClassContext } from '../../context/ClassContext';
import { ClassRecord, Roles, SubClassRecord } from '../../context/types';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls, processApiPath } from '../../utils/url';
import { StyledActionButton } from '../../views/AdminSettings/styles/table-styles';
import { DeleteConfirmationDialog } from '../common/dialog/DeleteConfirmationDialog';
import { GeneralDialog } from '../common/dialog/GeneralDialog';

const ActionsBox = styled(Stack)`
    flex-direction: row;
    align-items: center;
    gap: 4px;
`;

const ButtonIconBox = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-width: 20px;
    margin-right: 0.3rem;
`;

const EnterButton = styled(Button)`
    & .subclass-enter-button {
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
    }

    &:hover .subclass-enter-button {
        opacity: 1;
    }
`;

const ListItemIconStyled = styled(ListItemIcon)`
    min-width: 3rem;
`;

interface ClassRowProps {
    currentClassUuid: string;
    subClass: Partial<SubClassRecord>;
    isMain?: boolean;
    showHost?: boolean;
    isPrivilegedUser?: boolean;
    onClickOpen: (classroom: Partial<ClassRecord>) => void;
    onClickDelete: (classroom: Partial<ClassRecord>) => void;
    opening: string;
    key: string;
}

const ClassRow = (props: ClassRowProps) => {
    const { currentClassUuid, isMain, showHost, subClass, opening, isPrivilegedUser, onClickOpen, onClickDelete } =
        props;
    const { t } = useTranslation('class');
    const roomName = isMain ? t('sub-class.main-room') : subClass.class_name;
    const subClassUuid = subClass.class_uuid;
    const isCurrent = subClassUuid === currentClassUuid;

    return (
        <TableRow key={`subclass-table-row-${subClassUuid}`}>
            <TableCell key={`subclass-cell-name-${subClassUuid}`}>
                <EnterButton
                    size="medium"
                    color="primary"
                    variant="text"
                    disabled={!!opening || subClassUuid === currentClassUuid}
                    onClick={() => onClickOpen(subClass)}
                >
                    {opening === subClassUuid
                        ? [
                              <CircularProgress size={16} style={{ marginLeft: '0.3rem', marginRight: '0.3rem' }} />,
                              t('sub-class.opening')
                          ]
                        : [
                              roomName,
                              <MeetingRoom
                                  className="subclass-enter-button"
                                  style={{ marginLeft: '0.3rem' }}
                                  key={`subclass-enter-icon-${subClassUuid}`}
                              />
                          ]}
                </EnterButton>
            </TableCell>
            {showHost && (
                <TableCell key={`subclass-cell-host-${subClassUuid}`}>
                    <Typography
                        variant="body2"
                        color={subClass.leadInstructor?.userName ? 'text.primary' : 'text.secondary'}
                    >
                        {subClass.leadInstructor?.userName || t('sub-class.no-host')}
                    </Typography>
                </TableCell>
            )}
            {isPrivilegedUser && (
                <TableCell key={`subclass-cell-actions-${subClassUuid}`}>
                    <ActionsBox>
                        {!isMain && (
                            <Tooltip title={isCurrent ? t('sub-class.delete-self-unavailable') : ''} arrow>
                                <Box>
                                    <StyledActionButton
                                        size="medium"
                                        color="error"
                                        variant="contained"
                                        disabled={isCurrent}
                                        onClick={() => onClickDelete(subClass)}
                                    >
                                        <ButtonIconBox>
                                            <NoMeetingRoom fontSize="small" />
                                        </ButtonIconBox>
                                        {t('delete')}
                                    </StyledActionButton>
                                </Box>
                            </Tooltip>
                        )}
                    </ActionsBox>
                </TableCell>
            )}
        </TableRow>
    );
};

export const SubClassesDialog = (props: DialogProps) => {
    const { onClose } = props;
    const { t } = useTranslation('class');
    const { subClasses, mainClass, fetchSubClasses } = useContainer(SubClassContainer);
    const { me, userUuid, classUuid } = useContext(ClassContext);
    const { api } = getUrls();
    const { enqueueSnackbar } = useSnackbar();
    const isInstructor = me.role === Roles.Teacher;
    const isAssistant = me.role === Roles.Assistant;
    const isPrivilegedUser = isInstructor || isAssistant;
    const showHost = Boolean(mainClass?.leadInstructor);

    const [createSubClassOpen, setCreateSubClassOpen] = useState(false);
    const [deleteConfirmationClass, setDeleteConfirmationClass] = useState<Partial<SubClassRecord> | null>(null);
    const [opening, setOpening] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const handleClose = useCallback(
        (event) => {
            onClose?.(event, 'backdropClick');
        },
        [onClose]
    );
    const handleCloseSubClassCreateDialog = () => {
        setCreateSubClassOpen(false);
    };

    const onClickOpen = async (classroom: Partial<ClassRecord>) => {
        if (!classroom.class_uuid) {
            console.error('classroom does not have a uuid', classroom);
            return;
        }

        setOpening(classroom.class_uuid);
        try {
            const {
                data: { code }
            } = await axios.post(
                `${api}${CREATE_LTI_CODE}`,
                { student_id: me.student_id },
                { headers: getUserUuidHeaders(userUuid) }
            );

            const url = new URL(window.location.href);

            const segments = url.pathname.split('/');
            segments[segments.length - 1] = classroom.class_uuid;

            url.searchParams.set('code', code);

            const newUrl = `${url.origin}${segments.join('/')}${url.search}`;
            window.open(newUrl, '_self');
        } catch (err) {
            console.log(err);
            const message = (err as any).response?.status === 401 ? t('error-unauthorized') : t('error-message');
            enqueueSnackbar(message, { variant: 'error' });
        }
        setOpening('');
    };

    const onClickDelete = (classroom: Partial<ClassRecord>) => {
        setDeleteConfirmationClass(classroom);
    };

    const handleCloseSubClassDeleteDialog = () => {
        setDeleteConfirmationClass(null);
    };

    const handleClassDelete = async () => {
        try {
            setIsLoading(true);
            const url = processApiPath(CLASS_DELETE, {}, { meeting_id: deleteConfirmationClass!.meeting_id! });
            await axios.delete(url, { headers: getUserUuidHeaders(userUuid) });
            await fetchSubClasses();
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('error-delete'), { variant: 'error' });
        }
        setIsLoading(false);
        handleCloseSubClassDeleteDialog();
    };

    useEffect(() => {
        fetchSubClasses();
        setIsLoading(false);
    }, [fetchSubClasses]);

    return (
        <>
            <SubClassesCreateDialog open={createSubClassOpen} onClose={handleCloseSubClassCreateDialog} />
            <DeleteConfirmationDialog
                title={t('sub-class.delete-title')}
                open={Boolean(deleteConfirmationClass)}
                onConfirm={handleClassDelete}
                onClose={handleCloseSubClassDeleteDialog}
            >
                <Typography variant="body1">{t('sub-class.delete-confirm-message')}</Typography>
                <List>
                    <ListItem key={`subclass-delete-row-class-name`} disablePadding>
                        <ListItemIconStyled key={'subclass-delete-class-name-icon'}>
                            <MeetingRoom fontSize="large" />
                        </ListItemIconStyled>
                        <ListItemText
                            primary={deleteConfirmationClass?.class_name}
                            secondary={deleteConfirmationClass?.class_description || ''}
                            key={'subclass-delete-class-name-text'}
                        />
                    </ListItem>
                    {deleteConfirmationClass?.leadInstructor && (
                        <ListItem key={`subclass-delete-row-host`} disablePadding>
                            <ListItemIconStyled key={'subclass-delete-host-icon'}>
                                <Person3 fontSize="large" />
                            </ListItemIconStyled>
                            <ListItemText
                                primary={deleteConfirmationClass.leadInstructor.userName}
                                secondary={t('sub-class.host')}
                                key={'subclass-delete-host-text'}
                            />
                        </ListItem>
                    )}
                </List>
            </DeleteConfirmationDialog>
            <GeneralDialog
                open={props.open}
                onClose={handleClose}
                isLoading={isLoading}
                title={t('sub-class.showing-all')}
                icon={
                    <>
                        <MeetingRoom color="disabled" key="subclass-switch-icon-left" />
                        <ArrowRightAlt
                            color="disabled"
                            style={{ marginLeft: '-0.4rem', marginRight: '-0.45rem' }}
                            key="subclass-switch-icon-arrow"
                        />
                        <MeetingRoom color="disabled" key="subclass-switch-icon-right" />
                    </>
                }
            >
                <Table>
                    <TableHead>
                        <TableRow key="subclass-header-rooms">
                            <TableCell>{t('name')}</TableCell>
                            {showHost && <TableCell>{t('sub-class.host')}</TableCell>}
                            {isPrivilegedUser && <TableCell style={{ width: '20%' }}>{t('actions')}</TableCell>}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {mainClass && (
                            <ClassRow
                                currentClassUuid={classUuid}
                                subClass={mainClass}
                                isMain={true}
                                showHost={showHost}
                                opening={opening}
                                isPrivilegedUser={isPrivilegedUser}
                                onClickOpen={onClickOpen}
                                onClickDelete={onClickDelete}
                                key={`subclass-row-${mainClass.class_uuid}`}
                            />
                        )}
                        {subClasses?.map((subClass) => (
                            <ClassRow
                                currentClassUuid={classUuid}
                                subClass={subClass}
                                showHost={showHost}
                                opening={opening}
                                isPrivilegedUser={isPrivilegedUser}
                                onClickOpen={onClickOpen}
                                onClickDelete={onClickDelete}
                                key={`subclass-row-${subClass.class_uuid}`}
                            />
                        ))}
                    </TableBody>
                </Table>
                <DialogActions>
                    <StyledActionButton size="medium" color="primary" variant="outlined" onClick={handleClose}>
                        {t('common:close')}
                    </StyledActionButton>
                    {isPrivilegedUser && (
                        <StyledActionButton variant="contained" onClick={() => setCreateSubClassOpen(true)}>
                            <ButtonIconBox>
                                <Add fontSize="small" />
                            </ButtonIconBox>
                            {t('sub-class.create-button')}
                        </StyledActionButton>
                    )}
                </DialogActions>
            </GeneralDialog>
        </>
    );
};
